import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import type { ColumnMapping } from "@/types/backtest";

interface Field {
  key: keyof ColumnMapping;
  label: string;
  required: boolean;
  hint: string;
}

const FIELDS: Field[] = [
  {
    key: "supplierName",
    label: "Supplier / Vendor Name",
    required: true,
    hint: "e.g. 'Vendor', 'Supplier', 'Company'",
  },
  {
    key: "invoiceDate",
    label: "Invoice Date",
    required: true,
    hint: "Date the invoice was issued",
  },
  {
    key: "invoiceAmount",
    label: "Invoice Amount",
    required: true,
    hint: "Total amount due on the invoice",
  },
  { key: "paymentTerms", label: "Payment Terms", required: true, hint: 'e.g. "2/10 Net 30"' },
  {
    key: "actualPaymentDate",
    label: "Actual Payment Date",
    required: true,
    hint: "Date the invoice was actually paid",
  },
  {
    key: "invoiceNumber",
    label: "Invoice Number",
    required: false,
    hint: "Optional reference number",
  },
];

interface ColumnMapperProps {
  headers: string[];
  mapping: ColumnMapping;
  onChange: (mapping: ColumnMapping) => void;
}

const NONE = "__none__";

export function ColumnMapper({ headers, mapping, onChange }: ColumnMapperProps) {
  const set = (key: keyof ColumnMapping, value: string) => {
    onChange({ ...mapping, [key]: value === NONE ? null : value });
  };

  return (
    <div className="space-y-1">
      {FIELDS.map((field) => (
        <div
          key={field.key}
          className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 py-2 border-b border-border/40 last:border-0"
        >
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium">{field.label}</span>
              {field.required ? (
                <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                  Required
                </Badge>
              ) : (
                <Badge
                  variant="outline"
                  className="text-[10px] px-1 py-0 h-4 text-muted-foreground"
                >
                  Optional
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{field.hint}</p>
          </div>

          <span className="text-muted-foreground text-sm">→</span>

          <Select value={mapping[field.key] ?? NONE} onValueChange={(v) => set(field.key, v)}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Select column…" />
            </SelectTrigger>
            <SelectContent>
              {!field.required && (
                <SelectItem value={NONE}>
                  <span className="text-muted-foreground italic">None</span>
                </SelectItem>
              )}
              {headers.map((h) => (
                <SelectItem key={h} value={h}>
                  {h}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}
